import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "../components/Card";
import { db } from "../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";

const Housing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [housing, setHousing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    roomType: "",
    priceRange: "",
    housingType: "",
    furnished: false,
    petsAllowed: false,
    utilitiesIncluded: false,
    internetIncluded: false,
  });

  // Firestore'dan konut ilanlarını çek
  useEffect(() => {
    const fetchHousing = async () => {
      try {
        // Önce tüm kullanıcıları çek
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);
        const usersMap = {};
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          usersMap[userData.uid] = userData;
        });

        const q = query(
          collection(db, "listings"),
          where("listingType", "==", "housing"),
          where("status", "==", "active")
        );

        const querySnapshot = await getDocs(q);
        const housingData = querySnapshot.docs.map((doc) => {
          const houseData = doc.data();
          const username = usersMap[houseData.userId]?.username;
          const capitalizedUsername = username
            ? username.charAt(0).toUpperCase() + username.slice(1)
            : "Username Not Available";
          return {
            id: doc.id,
            ...houseData,
            companyName: capitalizedUsername,
            createdAt: houseData.createdAt?.toDate() || new Date(),
          };
        });

        // Tarihe göre sırala (en yeni en üstte)
        housingData.sort((a, b) => b.createdAt - a.createdAt);

        setHousing(housingData);
        setError("");
      } catch (err) {
        console.error("Error fetching housing:", err);
        setError("Failed to load housing listings. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchHousing();
  }, []);

  // Filtreleme fonksiyonu
  const filteredHousing = housing.filter((house) => {
    const matchesSearch =
      house.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      house.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      house.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilters =
      (!selectedFilters.roomType ||
        house.roomType === selectedFilters.roomType) &&
      (!selectedFilters.housingType ||
        house.housingType === selectedFilters.housingType) &&
      (!selectedFilters.furnished || house.furnished === true) &&
      (!selectedFilters.petsAllowed || house.petsAllowed === true) &&
      (!selectedFilters.utilitiesIncluded ||
        house.utilitiesIncluded === true) &&
      (!selectedFilters.internetIncluded || house.internetIncluded === true);

    return matchesSearch && matchesFilters;
  });

  // Filtreleri sıfırla
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedFilters({
      roomType: "",
      priceRange: "",
      housingType: "",
      furnished: false,
      petsAllowed: false,
      utilitiesIncluded: false,
      internetIncluded: false,
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-[#F8F9FA] p-4 flex items-center justify-center'>
        <div className='text-xl font-architects-daughter text-gray-600'>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen bg-[#F8F9FA] p-4 flex items-center justify-center'>
        <div className='text-xl font-architects-daughter text-red-600'>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#F8F9FA] p-4'>
      {/* Tabs */}
      <div className='max-w-7xl mx-auto mb-8'>
        <div className='flex gap-0'>
          <button
            onClick={() => navigate("/jobs")}
            className={`w-1/2 py-4 text-xl font-architects-daughter border-2 border-blue-600 rounded-l hover:bg-blue-50 cursor-pointer transition-colors
              ${location.pathname === "/jobs" ? "bg-blue-100" : ""}`}>
            Jobs
          </button>
          <button
            onClick={() => navigate("/housing")}
            className={`w-1/2 py-4 text-xl font-architects-daughter border-2 border-l-0 border-blue-600 rounded-r hover:bg-blue-50 cursor-pointer transition-colors
              ${location.pathname === "/housing" ? "bg-blue-100" : ""}`}>
            Housing
          </button>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className='max-w-7xl mx-auto px-4 flex flex-col md:flex-row'>
        {/* Filtreler - Desktop */}
        <div className='hidden md:block w-56 mr-8'>
          <div className='sticky top-4'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-xl font-architects-daughter'>Filters</h2>
              <button
                onClick={clearFilters}
                className='text-blue-600 text-sm font-architects-daughter hover:underline'>
                Clear All
              </button>
            </div>

            {/* Room Type Filter */}
            <div className='mb-6'>
              <h3 className='font-architects-daughter mb-2'>Room Type</h3>
              <div className='space-y-2'>
                {["shared", "private"].map((type) => (
                  <label key={type} className='flex items-center'>
                    <input
                      type='radio'
                      name='roomType'
                      checked={selectedFilters.roomType === type}
                      onChange={() =>
                        setSelectedFilters((prev) => ({
                          ...prev,
                          roomType: type,
                        }))
                      }
                      className='mr-2'
                    />
                    <span className='font-architects-daughter text-sm capitalize'>
                      {type} Room
                    </span>
                  </label>
                ))}
                <label className='flex items-center'>
                  <input
                    type='radio'
                    name='roomType'
                    checked={selectedFilters.roomType === ""}
                    onChange={() =>
                      setSelectedFilters((prev) => ({ ...prev, roomType: "" }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    All Types
                  </span>
                </label>
              </div>
            </div>

            {/* Additional Filters */}
            <div className='mb-6'>
              <h3 className='font-architects-daughter mb-2'>
                Additional Filters
              </h3>
              <div className='space-y-2'>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.furnished}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        furnished: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Furnished
                  </span>
                </label>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.petsAllowed}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        petsAllowed: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Pets Allowed
                  </span>
                </label>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.utilitiesIncluded}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        utilitiesIncluded: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Utilities Included
                  </span>
                </label>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={selectedFilters.internetIncluded}
                    onChange={(e) =>
                      setSelectedFilters((prev) => ({
                        ...prev,
                        internetIncluded: e.target.checked,
                      }))
                    }
                    className='mr-2'
                  />
                  <span className='font-architects-daughter text-sm'>
                    Internet Included
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Ana İçerik */}
        <div className='flex-1'>
          {/* Arama Kutusu */}
          <div className='mb-6'>
            <div className='relative'>
              <input
                type='text'
                placeholder='Search housing by location or description...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full p-3 border-2 border-gray-200 rounded-lg font-architects-daughter focus:outline-none focus:border-blue-500'
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'>
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Mobil Filtreler */}
          <div className='md:hidden mb-6'>
            <select
              value={selectedFilters.roomType}
              onChange={(e) =>
                setSelectedFilters((prev) => ({
                  ...prev,
                  roomType: e.target.value,
                }))
              }
              className='w-full p-2 border rounded focus:outline-none focus:border-blue-500'>
              <option value=''>All Room Types</option>
              <option value='shared'>Shared Room</option>
              <option value='private'>Private Room</option>
            </select>
          </div>

          {/* İlanların Listesi */}
          <div className='mb-4 text-gray-600 font-architects-daughter'>
            {filteredHousing.length} housing listing
            {filteredHousing.length !== 1 ? "s" : ""} found
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {filteredHousing.map((house) => (
              <Card
                key={house.id}
                id={house.id}
                type='housing'
                title={house.title}
                subtitle={house.location}
                tags={[
                  house.rent ? `$${house.rent}/week` : "Price not available",
                  house.deposit
                    ? `$${house.deposit} deposit`
                    : "Deposit not available",
                ]}
                date={house.createdAt.toLocaleDateString()}
                images={house.images}
                companyName={house.companyName}
              />
            ))}
          </div>

          {filteredHousing.length === 0 && (
            <div className='text-center py-12'>
              <p className='text-gray-600 font-architects-daughter'>
                No housing listings found matching your criteria
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Housing;
